import React, { Component, ReactNode } from 'react';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import { captureEnhancedErrorContext } from '@lib/utils/enhancedErrorContext';
import { Flex, Text, Button } from '@servicetitan/anvil2';
import environment from '@lib/environment';

export interface GlobalErrorBoundaryProps {
  children: ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * GlobalErrorBoundary - Application-level error boundary that catches
 * critical errors and integrates with DiagnosticsModule for error reporting.
 *
 * Features:
 * - Catches errors at the application root level
 * - Reports errors to DiagnosticsModule (which sends to Sentry)
 * - Displays user-friendly error page with reload option
 * - Shows error details in development mode
 *
 * Usage:
 * Wrap your entire application with this boundary at the root level.
 *
 * @example
 * <GlobalErrorBoundary>
 *   <App />
 * </GlobalErrorBoundary>
 */
export class GlobalErrorBoundary extends Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<GlobalErrorBoundaryState> {
    // Check if error boundaries should be enabled in development
    const enableInDev = import.meta.env.VITE_ENABLE_ERROR_BOUNDARIES === 'true';

    // In local development, don't catch UNLESS explicitly enabled for testing
    if (environment.MODE === 'development' && !enableInDev) {
      return {}; // Return empty state, don't set hasError - show React overlay
    }

    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Always log for debugging (helpful in all environments)
    console.error('[GlobalErrorBoundary] caught error:', error, errorInfo);

    // Capture enhanced error context (url, pathname, feature flags, etc.)
    const enhancedContext = captureEnhancedErrorContext();

    // Report error to DiagnosticsModule which will:
    // 1. Send to Sentry for tracking (only in non-dev/test environments)
    // 2. Log to console
    // 3. Add to diagnostics state for debugging
    DiagnosticsModule.reportUserError(
      error,
      `Application Error: ${error.message}`,
      {
        priority: 'modal',
        source: 'global-error-boundary',
        extra: {
          componentStack: errorInfo.componentStack,
          errorInfo,
          // Enhanced context for better debugging
          url: enhancedContext.url,
          pathname: enhancedContext.pathname,
          timestamp: enhancedContext.timestamp,
          userAgent: enhancedContext.userAgent,
          featureFlags: enhancedContext.featureFlags,
        },
      },
    );

    // Note: Environment-based error boundary behavior is controlled in getDerivedStateFromError,
    // not here. componentDidCatch should only perform side effects (logging, reporting).
    // Re-throwing here would corrupt React's internal state.
  }

  handleReload = (): void => {
    // Note: No confirmation dialog here because global errors represent catastrophic
    // failures where reload is the only recovery option
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Flex
          data-testid='global-error-fallback'
          direction='column'
          alignItems='center'
          justifyContent='center'
          className='min-h-screen p-5 ta-center bg-neutral-100'
        >
          <Flex
            direction='column'
            gap={3}
            className='max-w-600 p-5 bg-white br-1 shadow-1'
          >
            <Text el='h1' variant='headline' size='large' className='c-red-600'>
              Something went wrong
            </Text>

            <Text variant='body'>
              We're sorry, but something unexpected happened. The error has been
              reported to our team.
            </Text>

            {this.state.error && (
              <details
                data-testid='error-details'
                className='ta-left p-2 bg-neutral-100 br-1'
              >
                <summary className='cursor-pointer fw-500 m-b-1'>
                  Error Details
                </summary>
                <code className='d-block fs-small c-red-600 word-break-all'>
                  {this.state.error.message}
                </code>
              </details>
            )}

            <Button
              data-testid='reload-button'
              onClick={this.handleReload}
              appearance='primary'
            >
              Reload Page
            </Button>
          </Flex>
        </Flex>
      );
    }

    return this.props.children;
  }
}
